
import React, { useState, useRef, useCallback } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { buildD1Payload } from '../../../services/payloads/d1Payload';
import { getCurrentUserEmail, getCurrentUserName } from '../../../services/auth';
import { d1Logger, emailLogger } from '../../../services/logger';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Alert,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import './d1.css';
import '../../../css/tooltip.css';
import { getEightDInfoId, setEightDInfoId } from '../../../services/eightDInfoStore';
import { fetchApi } from "../../../services/api";
import { getEmployeeData } from '../../../services/employeeStore';
/**
 * Props:
 *  - expanded: boolean
 *  - members: Array<{ email: string; name: string; mobile?: string }>
 *  - newMember: { email: string; name: string; mobile?: string }
 *  - handleCloseForm: () => void
 *  - setNewMember: (val) => void
 *  - setMembers: (val) => void
 *  - onFillClick: () => void
 *  - onToggleAccordion: () => void
 *  - eightDInfoId: number              // <-- REQUIRED for API payload
 *  - currentUserEmail: string          // <-- REQUIRED for audit fields
 *  - apiBaseUrl?: string               // optional (e.g., "/backend")
 *  - onSaved?: (saved) => void         // optional callback on success
 */
const D1 = ({
  expanded,
  members,
  newMember,
  handleCloseForm,
  setNewMember,
  setMembers,
  onFillClick,
  onToggleAccordion,
  eightDInfoId,
  currentUserEmail = getCurrentUserEmail(),
  onSaved,
  isEnabled = false,
  d1Saved = false,
  d1Data,
  isDrafted = false,
  readOnly = false,
}) => {
  const effectiveEnabled = isEnabled && !readOnly;
  const canView = isEnabled;
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isUpdate, setIsUpdate] = useState(false);
  const [deletedMembers, setDeletedMembers] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);

  // Fetch roles from RoleMaster
  React.useEffect(() => {
    const fetchRoleOptions = async () => {
      try {
        const response = await fetchApi('/api/RoleMaster/GetRoles', { method: 'GET' });
        let rolesData = [];
        if (Array.isArray(response)) rolesData = response;
        else if (response.data && Array.isArray(response.data)) rolesData = response.data;
        else if (response.result && Array.isArray(response.result)) rolesData = response.result;
        else if (response.roles && Array.isArray(response.roles)) rolesData = response.roles;

        const activeRoles = rolesData
          .filter(role => role.Status === 'Active' && role.RoleDescription?.toLowerCase() !== 'originator')
          .map(role => ({ code: role.RoleCode, description: role.RoleDescription }));
        setRoleOptions(activeRoles);
      } catch (error) {
        console.error('[D1] GetRoles Error:', error);
      }
    };
    fetchRoleOptions();
  }, []);

  // Detect if D1 data already exists (edit mode)
  React.useEffect(() => {
    if (d1Data && Array.isArray(d1Data) && d1Data.length > 0) {
      setIsUpdate(true);
    } else if (d1Data?.data && Array.isArray(d1Data.data) && d1Data.data.length > 0) {
      setIsUpdate(true);
    }
  }, [d1Data]);

  // Auto-populate originator from employeeStore when members list is empty
  React.useEffect(() => {
    if (members.length === 0 && !isUpdate) {
      const emp = getEmployeeData();
      const email = currentUserEmail || getCurrentUserEmail() || sessionStorage.getItem('userEmail') || '';
      if (email) {
        setMembers([{
          email,
          name: emp?.shortName || emp?.teamMemberName || emp?.name || getCurrentUserName() || '',
          mobile: emp?.mobileNo || emp?.mobile || '',
          role: 'o',
          division: emp?.divCode || emp?.Division_Code || '',
        }]);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [editIndex, setEditIndex] = useState(null);
  const [editBackup, setEditBackup] = useState(null);

  // Utility function to refresh member data from server
  const refreshMemberData = async (eightDInfoId) => {
    try {
      const response = await fetchApi('/api/D1/GetD1', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'X-Timestamp': new Date().getTime().toString()
        },
        body: JSON.stringify({ EightDInfoId: eightDInfoId }),
      });
      
      if (response?.data && Array.isArray(response.data)) {
        const mapRole = (member) => {
          const mr = (member.MemberRole || '').toLowerCase();
          if (mr === 'originator') return 'o';
          return '';
        };
        
        const mapMember = (member) => ({
          d1Id: member.D1Id || null,
          email: member.EmailId || '',
          name: member.TeamMemberName || '',
          mobile: member.MobileNo || '',
          role: mapRole(member),
          memberRole: (member.MemberRole || '').toLowerCase() === 'originator' ? '' : (member.MemberRole || ''),
          division: member.Division || '',
          createdBy: member.CreatedBy,
          createdOn: member.CreatedOn,
          modifiedBy: member.ModifiedBy,
          modifiedOn: member.ModifiedOn
        });
        
        const roleOrder = { o: 0, c: 1 };
        const refreshedMembers = response.data.map(mapMember).sort((a, b) => {
          const aOrder = roleOrder[a.role] ?? 2;
          const bOrder = roleOrder[b.role] ?? 2;
          return aOrder - bOrder || (a.d1Id || 0) - (b.d1Id || 0);
        });
        setMembers(refreshedMembers);
        return refreshedMembers;
      }
    } catch (error) {
      console.warn('Failed to refresh member data:', error);
    }
    return null;
  };

  // Auto-open edit mode for members with empty name
  React.useEffect(() => {
    if (expanded && editIndex === null && members.length > 0) {
      const emptyNameIdx = members.findIndex(m => !m.name || !m.name.trim());
      if (emptyNameIdx !== -1) {
        setEditBackup({ ...members[emptyNameIdx] });
        setEditIndex(emptyNameIdx);
        setEditErrors({ email: '', name: 'Name is required.', mobile: '' });
      }
    }
  }, [expanded, members]);
  const [showNewRow, setShowNewRow] = useState(false);
  const [errors, setErrors] = useState({ email: '', name: '', mobile: '' });
  const [editErrors, setEditErrors] = useState({ email: '', name: '', mobile: '' });

  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const debounceRef = useRef(null);

  const fetchEmailSuggestions = useCallback((term) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!term || term.length < 2) { setEmailSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetchApi('/api/D1/GetEmailAutoComplete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ emailID: term, type: 'ALL' }),
        });
        const list = res?.model || res?.data || res || [];
        setEmailSuggestions(Array.isArray(list) ? list : []);
      } catch { setEmailSuggestions([]); }
    }, 500);
  }, []);

  // State to force re-render when store changes
  const [storeValue, setStoreValue] = useState(getEightDInfoId());

  // Print EightDInfoId in terminal on component mount and updates
  React.useEffect(() => {
    const currentId = eightDInfoId || getEightDInfoId();
    
    // Update local state to trigger re-render
    setStoreValue(getEightDInfoId());
  }, [eightDInfoId]);

  // NEW: Save state & notices
  const [saving, setSaving] = useState(false);
  const [formSaved, setFormSaved] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveNotice, setSaveNotice] = useState(null);

  // Validation logic for edit
  const validateEditField = (field, value) => {
    let error = '';
    if (field === 'email') {
      const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
      if (!value) error = 'Email is required';
      else if (!emailRegex.test(value)) error = 'Invalid email format';
    }
    if (field === 'name') {
      if (!value) error = 'Name is required';
    }
    if (field === 'mobile') {
      if (value && !/^[0-9]{10}$/.test(value)) error = 'Must be 10 digits';
    }
    setEditErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleChange = (index, field, value) => {
    const updated = [...members];
    updated[index][field] = value;
    setMembers(updated);
    validateEditField(field, value);
  };

  // Disable Check button until edit is valid:
  const isEditValid = () => {
    return (
      !editErrors.email &&
      !editErrors.name &&
      !editErrors.mobile &&
      members[editIndex].email &&
      members[editIndex].name
    );
  };

  // Validation logic for new row
  const validateField = (field, value) => {
    let error = '';
    if (field === 'email') {
      const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
      if (!value) error = 'Email is required';
      else if (!emailRegex.test(value)) error = 'Invalid email format';
    }
    if (field === 'name') {
      if (!value) error = 'Name is required';
    }
    if (field === 'mobile') {
      if (value && !/^[0-9]{10}$/.test(value)) error = 'Must be 10 digits';
    }
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleFieldChange = (field, value) => {
    setNewMember({ ...newMember, [field]: value });
    validateField(field, value);
  };

  const isFormValid = () => {
    return (
      newMember.email &&
      newMember.name &&
      !errors.email &&
      !errors.name &&
      !errors.mobile &&
      (members.length === 0 || newMember.memberRole)
    );
  };

  const isGodrejEmail = (email) => email && email.toLowerCase().endsWith('@godrej.com');

  const isDuplicateEmail = (email, excludeIndex = -1) =>
    members.some((m, i) => i !== excludeIndex && m.email.toLowerCase() === email.toLowerCase());

  const hasOriginator = members.some(m => m.role === 'o');
  const hasCoordinator = members.some(m => m.role === 'c');

  // Add new member
  const handleAddMember = () => {
    if (!isFormValid()) {
      alert('Please fix validation errors before saving.');
      return;
    }
    
    // Check for duplicate email
    if (isDuplicateEmail(newMember.email)) {
      alert('Team member already exist.');
      return;
    }
    
    // Originator is auto-assigned for first member only
    const role = members.length === 0 ? 'o' : (newMember.memberRole || '');
    
    // Originator must be Godrej user
    if (role === 'o' && !isGodrejEmail(newMember.email)) {
      alert('Originator must be a Godrej User');
      return;
    }
    
    setMembers([...members, { ...newMember, role, memberRole: newMember.memberRole || '' }]);
    setNewMember({ email: '', name: '', mobile: '', division: '', memberRole: '' });
    setErrors({ email: '', name: '', mobile: '' });
    setShowNewRow(false); // Hide row after adding
  };

  const handleRemoveMember = (index) => {
    const memberToDelete = members[index];
    
    // Show confirmation dialog
    if (!window.confirm(`Are you sure you want to remove ${memberToDelete.name || memberToDelete.email} from the team?`)) {
      return;
    }
    
    // Track deleted members that exist in DB (have d1Id) so we can send IsDeleted: true
    if (memberToDelete.d1Id) {

      setDeletedMembers(prev => [...prev, memberToDelete]);
    } else {

    }
    
    const updatedMembers = members.filter((_, i) => i !== index);
    
    // Keep originator fixed, others retain their selected role
    const membersWithRoles = updatedMembers.map((member, newIndex) => ({
      ...member,
      role: newIndex === 0 ? 'o' : member.role
    }));
    
    setMembers(membersWithRoles);
  };

  // Cancel new member entry
  const handleCancel = () => {
    setNewMember({ email: '', name: '', mobile: '', division: '', memberRole: '' });
    setErrors({ email: '', name: '', mobile: '' });
    setShowNewRow(false); // Hide row
  };

  const toggleAccordion = () => {
    onToggleAccordion();
  };

  
  const hasClientErrors = () => {
    // quick check: email/name required; mobile optional but if present must be 10 digits
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    for (const m of members) {
      if (!m.email || !emailRegex.test(m.email)) return true;
      if (!m.name || !m.name.trim()) return true;
      if (m.mobile && !/^[0-9]{10}$/.test(m.mobile)) return true;
    }
    return false;
  };

  const handleSave = async () => {
    d1Logger.saveStart({
      membersCount: members.length,
      eightDInfoId: eightDInfoId || getEightDInfoId(),
      isUpdate
    });
    





    
    const currentEightDInfoId = eightDInfoId || getEightDInfoId();
    setSaveError(null);
    setSaveNotice(null);

    if (!members || members.length === 0) {

      d1Logger.saveError('No members to save');
      setSaveError('Please add at least one team member before saving.');
      setTimeout(() => setSaveError(null), 2000);
      return;
    }

    if (members.length < 2) {

      d1Logger.saveError('Need at least 2 members');
      setSaveError('Please add at least one more team member');
      setTimeout(() => setSaveError(null), 2000);
      return;
    }

    // Check for duplicate emails
    const emails = members.map(m => m.email.toLowerCase());
    const duplicateEmails = emails.filter((email, index) => emails.indexOf(email) !== index);
    if (duplicateEmails.length > 0) {

      d1Logger.saveError({ duplicateEmails });
      setSaveError('Duplicate team members are not allowed.');
      setTimeout(() => setSaveError(null), 2000);
      return;
    }

    // Keep originator fixed, others retain their selected memberRole
    const updatedMembers = members.map((member, index) => ({
      ...member,
      role: index === 0 ? 'o' : member.role
    }));
    setMembers(updatedMembers);


    // Ensure Originator is a Godrej user
    const originatorMember = updatedMembers.find(m => m.role === 'o');
    
    if (originatorMember && !isGodrejEmail(originatorMember.email)) {

      d1Logger.saveError({ issue: 'Originator not Godrej user', email: originatorMember.email });
      setSaveError('Originator must be a Godrej User');
      setTimeout(() => setSaveError(null), 2000);
      return;
    }

    // Consolidated validation over all rows
    if (hasClientErrors()) {
      const missingName = members.find(m => !m.name || !m.name.trim());
      if (missingName) {

        d1Logger.saveError({ issue: 'Missing name', email: missingName.email });
        setSaveError(`Name is required for ${missingName.email || 'a team member'}.`);
        setTimeout(() => setSaveError(null), 2000);
      } else {

        d1Logger.saveError('Client validation errors');
        setSaveError('Please fix validation errors (email/name required; mobile must be 10 digits if provided).');
        setTimeout(() => setSaveError(null), 2000);
      }
      return;
    }

    if (!currentUserEmail) {

      d1Logger.saveError('Missing currentUserEmail');
      setSaveError('currentUserEmail is missing.');
      setTimeout(() => setSaveError(null), 2000);
      return;
    }

    setSaving(true);
    try {

      // Modified by Vinaya - await async buildD1Payload (KMS encryption of mobile no.)
      const payload = await buildD1Payload({
        members: updatedMembers,
        deletedMembers,
        eightDInfoId: currentEightDInfoId,
        currentUserEmail,
      });

      

      
      const endpoint = isUpdate ? '/api/D1/UpdateD1' : '/api/D1/CreateD1';

      
      const data = await fetchApi(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      

      setIsUpdate(true);
      
      // Update members with response data, filtering out IsDeleted records
      const respMembers = (data?.data || []).filter(m => !m.IsDeleted);
      if (respMembers.length > 0) {
        const updated = updatedMembers.map((m, i) => {
          const respMember = respMembers[i];
          return {
            ...m,
            d1Id: respMember?.D1Id || m.d1Id || null,
            createdBy: respMember?.CreatedBy || m.createdBy,
            createdOn: respMember?.CreatedOn || m.createdOn,
            modifiedBy: respMember?.ModifiedBy || currentUserEmail,
            modifiedOn: respMember?.ModifiedOn || new Date().toISOString()
          };
        });
        setMembers(updated);
      }
      
      // Clear deleted members after successful save
      setDeletedMembers([]);
      setSaveNotice("Team members saved successfully.");
      setTimeout(() => setSaveNotice(null), 2000);
      setFormSaved(true);
      onSaved && onSaved(data);
      
      d1Logger.saveSuccess({ membersCount: updatedMembers.length, reportNo: currentEightDInfoId });

      // Email notification is now handled by the backend D1Service automatically








      
      emailLogger.teamCreation({ message: 'Email handled by backend', reportNo: currentEightDInfoId });


    } catch (e) {
      console.error('[Frontend] D1 save error:', e);
      d1Logger.saveError(e);
      setSaveError(e?.message || 'Failed to save team members.');
      setTimeout(() => setSaveError(null), 2000);
    } finally {
      setSaving(false);
    }
  };
  // -------------------------------------------------------

  return (
    <>
      <div
        className={`d1-header-container ${!canView ? 'disabled' : ''}`}
        onClick={canView ? toggleAccordion : undefined}
        style={{ cursor: canView ? 'pointer' : 'not-allowed', opacity: canView ? 1 : 0.5 }}
      >
        <div className="d1-header-left">
          <div className="d1-circle" style={(formSaved || (d1Saved && hasCoordinator && !isDrafted)) && !expanded ? { backgroundColor: '#4ccb91', borderColor: '#4ccb91' } : (effectiveEnabled || expanded ? { backgroundColor: '#f8c239', borderColor: '#f8c239' } : {})}>
          </div>
          <span className="d1-title">D1</span>
          <span className="d1-subtitle">Use a Team Approach</span>

          {/* Tooltip on Info Icon */}
          <Tooltip
            title={
              <span>
                The Problem Solving Investigator uses a multi-person approach to collect information
                from those directly associated with and/or impacted by the problem. The Team members'
                names and contact info should be listed here.
              </span>
            }
            arrow
            placement="bottom-start"
            PopperProps={{
              modifiers: [
                {
                  name: 'offset',
                  options: { offset: [0, 8] },
                },
              ],
            }}
            componentsProps={{
              tooltip: { className: 'custom-tooltip' },
              arrow: { className: 'custom-arrow' },
            }}
          >
            <IconButton>
              <InfoOutlinedIcon sx={{ color: '#666' }} />
            </IconButton>
          </Tooltip>
        </div>

        <button className="d1-fill-btn" onClick={(e) => { e.stopPropagation(); onFillClick(); }} disabled={!effectiveEnabled} style={{ opacity: effectiveEnabled ? 1 : 0.5 }}>
          {d1Saved ? 'Edit' : 'Fill'}
        </button>
      </div>

      {/* Accordion Form */}
      {expanded && (
        <Accordion expanded={expanded} sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box className="accordion-close-button-container">
              <Box
                className="close-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseForm();
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  backgroundColor: '#f50057',
                  color: '#fff',
                  padding: '6px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#c51162' }
                }}
              >
                <CloseIcon />
                Close Form
              </Box>
            </Box>
          </AccordionSummary>

          <AccordionDetails>
            <Box sx={{ width: '100%', maxWidth: 1000, margin: 'auto', mt: 3 }}>
             

              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography className="d1-team-header">
                  Add Team Member Email ID's and Contact No's
                </Typography>
               

                {!showNewRow && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton sx={{ color: '#800040' }} onClick={() => setShowNewRow(true)}>
                      <AddIcon />
                    </IconButton>
                    <Typography className="d1-team-header">Add Member</Typography>
                  </Box>
                )}
              </Box>

              {/* Table Header */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr 1fr 50px',
                  borderBottom: '2px solid #800040',
                  pb: 1,
                  mb: 1,
                }}
              >
                <Typography className="d1-table-header-text">Email ID</Typography>
                <Typography className="d1-table-header-text">Team Member Name</Typography>
                <Typography className="d1-table-header-text">Mobile No.</Typography>
                <Typography className="d1-table-header-text">Role</Typography>
              </Box>

              {/* Existing Member Row */}
              {members.map((member, index) => (
                <Box
                  key={index}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr 1fr 80px',
                    alignItems: 'center',
                    py: 1,
                    borderBottom: '1px solid #eee',
                  }}
                >
                  {editIndex === index ? (
                    <>
                      <TextField
                        value={member.email}
                        onChange={(e) => {
                          handleChange(index, 'email', e.target.value);
                          fetchEmailSuggestions(e.target.value);
                        }}
                        variant="standard"
                        error={!!editErrors.email}
                        helperText={editErrors.email}
                        className="text-field-standard"
                        InputProps={{ readOnly: member.role === 'o' }}
                        onBlur={() => {
                          const match = emailSuggestions.find(s => s.emailId?.toLowerCase() === member.email?.toLowerCase());
                          if (match) {
                            handleChange(index, 'name', match.teamMemberName || '');
                            handleChange(index, 'mobile', match.mobileNo || '');
                            handleChange(index, 'division', match.divCode || match.Division_Code || '');
                          }
                          setEmailSuggestions([]);
                        }}
                        sx={{
                          '& .MuiInput-underline:after': {
                            borderBottomColor: '#800040',
                          },
                          '& .MuiInput-underline:before': {
                            borderBottomColor: '#ccc',
                          },
                          '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                            borderBottomColor: '#800040',
                          }
                        }}
                      />
                      <TextField
                        value={member.name}
                        onChange={(e) => handleChange(index, 'name', e.target.value)}
                        variant="standard"
                        error={!!editErrors.name}
                        helperText={editErrors.name}
                        className="text-field-standard"
                        sx={{
                          '& .MuiInput-underline:after': {
                            borderBottomColor: '#800040',
                          },
                          '& .MuiInput-underline:before': {
                            borderBottomColor: '#ccc',
                          },
                          '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                            borderBottomColor: '#800040',
                          }
                        }}
                      />
                      <TextField
                        value={member.mobile}
                        onChange={(e) => handleChange(index, 'mobile', e.target.value)}
                        variant="standard"
                        error={!!editErrors.mobile}
                        helperText={editErrors.mobile}
                        className="text-field-standard"
                        sx={{
                          '& .MuiInput-underline:after': {
                            borderBottomColor: '#800040',
                          },
                          '& .MuiInput-underline:before': {
                            borderBottomColor: '#ccc',
                          },
                          '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                            borderBottomColor: '#800040',
                          }
                        }}
                      />
                      {member.role === 'o' ? (
                        <Typography className="d1-table-header-text">Originator</Typography>
                      ) : (
                        <TextField
                          select
                          value={member.memberRole || ''}
                          onChange={(e) => handleChange(index, 'memberRole', e.target.value)}
                          variant="standard"
                          className="text-field-standard"
                          SelectProps={{ native: true }}
                          sx={{
                            '& .MuiInput-underline:after': { borderBottomColor: '#800040' },
                            '& .MuiInput-underline:before': { borderBottomColor: '#ccc' },
                            '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: '#800040' }
                          }}
                        >
                          <option value="">Select Role</option>
                          {roleOptions.map(opt => (
                            <option key={opt.code} value={opt.code}>{opt.description}</option>
                          ))}
                        </TextField>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <IconButton onClick={() => {
                          if (isDuplicateEmail(members[editIndex].email, editIndex)) {
                            alert('Team member already exist.');
                            return;
                          }
                          // Only check Godrej email requirement for Originator
                          if (members[editIndex].role === 'o' && !isGodrejEmail(members[editIndex].email)) {
                            alert('Originator must be a Godrej User');
                            return;
                          }
                          setEditIndex(null);
                          setEditBackup(null);
                        }} disabled={!isEditValid()}>
                          <CheckIcon />
                        </IconButton>
                        <IconButton onClick={() => {
                          if (editBackup) {
                            const updated = [...members];
                            updated[editIndex] = { ...editBackup };
                            setMembers(updated);
                          }
                          setEditIndex(null);
                          setEditBackup(null);
                          setEditErrors({ email: '', name: '', mobile: '' });
                        }}>
                          <ClearIcon />
                        </IconButton>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Typography className="d1-table-header-text">{member.email}</Typography>
                      <Typography className="d1-table-header-text" sx={!member.name?.trim() ? { color: '#d32f2f', fontStyle: 'italic' } : {}}>
                        {member.name?.trim() || 'Name is required.'}
                      </Typography>
                      <Typography className="d1-table-header-text">{member.mobile}</Typography>
                      <Typography className="d1-table-header-text">
                        {member.role === 'o' ? 'Originator' : (roleOptions.find(r => r.code === member.memberRole)?.description || member.memberRole || '')}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <IconButton onClick={() => { setEditBackup({ ...member }); setEditIndex(index); }}>
                          <EditIcon sx={{ color: '#800040' }} />
                        </IconButton>
                        {/* Show delete button for non-essential roles (not Originator or Coordinator) */}
                        {member.role !== 'o' && member.role !== 'c' && (
                          <IconButton onClick={() => handleRemoveMember(index)}>
                            <DeleteIcon sx={{ color: '#800040' }} />
                          </IconButton>
                        )}
                      </Box>
                      {(member.role === 'o' || member.role === 'c') && (
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            backgroundColor: '#800040',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                            fontSize: '14px',
                          }}
                        >
                          {member.role === 'o' ? 'O' : 'C'}
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              ))}

              {/* New Member Row */}
              {showNewRow && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr 1fr 80px',
                    alignItems: 'center',
                    py: 2,
                  }}
                >
                  <Autocomplete
                    freeSolo
                    options={emailSuggestions}
                    getOptionLabel={(opt) => typeof opt === 'string' ? opt : (opt.emailId || '')}
                    inputValue={newMember.email}
                    onInputChange={(_, val) => {
                      handleFieldChange('email', val);
                      fetchEmailSuggestions(val);
                    }}
                    onChange={(_, selected) => {
                      if (selected && typeof selected === 'object') {
                        setNewMember({
                          ...newMember,
                          email: selected.emailId || '',
                          name: selected.teamMemberName || '',
                          mobile: selected.mobileNo || '',
                          division: selected.divCode || selected.Division_Code || '',
                        });
                        setEmailSuggestions([]);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Enter your email *"
                        variant="standard"
                        error={!!errors.email}
                        helperText={errors.email}
                        className="text-field-standard"
                        sx={{
                          '& .MuiInput-underline:after': {
                            borderBottomColor: '#800040',
                          },
                          '& .MuiInput-underline:before': {
                            borderBottomColor: '#ccc',
                          },
                          '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                            borderBottomColor: '#800040',
                          }
                        }}
                      />
                    )}
                  />
                  <TextField
                    label="Name *"
                    value={newMember.name}
                    variant="standard"
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    error={!!errors.name}
                    helperText={errors.name}
                    className="text-field-standard"
                    sx={{
                      '& .MuiInput-underline:after': {
                        borderBottomColor: '#800040',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: '#ccc',
                      },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: '#800040',
                      }
                    }}
                  />
                  <TextField
                    label="Contact No."
                    value={newMember.mobile}
                    variant="standard"
                    onChange={(e) => handleFieldChange('mobile', e.target.value)}
                    error={!!errors.mobile}
                    helperText={errors.mobile}
                    className="text-field-standard"
                    sx={{
                      '& .MuiInput-underline:after': {
                        borderBottomColor: '#800040',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: '#ccc',
                      },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: '#800040',
                      }
                    }}
                  />
                  <TextField
                    select
                    label="Role *"
                    value={newMember.memberRole || ''}
                    onChange={(e) => setNewMember({ ...newMember, memberRole: e.target.value })}
                    variant="standard"
                    className="text-field-standard"
                    SelectProps={{ native: true }}
                    sx={{
                      '& .MuiInput-underline:after': { borderBottomColor: '#800040' },
                      '& .MuiInput-underline:before': { borderBottomColor: '#ccc' },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: '#800040' }
                    }}
                  >
                    <option value="">Select Role</option>
                    {roleOptions.map(opt => (
                      <option key={opt.code} value={opt.code}>{opt.description}</option>
                    ))}
                  </TextField>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      sx={{
                        backgroundColor: '#800040',
                        color: '#fff',
                        borderRadius: '50%',
                      }}
                      onClick={handleAddMember}
                      disabled={!isFormValid()}
                    >
                      <CheckIcon />
                    </IconButton>
                    <IconButton
                      sx={{
                        backgroundColor: '#800040',
                        color: '#fff',
                        borderRadius: '50%',
                      }}
                      onClick={handleCancel}
                    >
                      <ClearIcon />
                    </IconButton>
                  </Box>
                </Box>
              )}

              {/* Save Button */}
              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: '#800040',
                    color: '#fff',
                    borderRadius: '20px',
                    textTransform: 'none',
                    px: 4,
                  }}
                  onClick={handleSave}
                  disabled={saving || readOnly}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </Box>

              {/* Save notices */}
              {saveError && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="error">{saveError}</Alert>
                </Box>
              )}
              {saveNotice && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="success">{saveNotice}</Alert>
                </Box>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
};

export default D1;
